/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import useStorage from 'src/sdk/hooks/useStorage'
import { datadogRum } from '@datadog/browser-rum-slim'
import { identifySentryUser } from 'src/utils/sentryUser'

import { axiosClient } from '../../../services/axiosInstance'
import { getUserSession } from './utils'
import type { Addresses, UserDecathlon } from '../types'

type AccountProviderProps = {
  children: ReactNode
}

type AccountContextProps = {
  isLoading: boolean
  userDecathlon: UserDecathlon | null
  setAlertSuccess: (item: boolean) => void
  updateUserDecathlon: () => Promise<void>
  alertSuccess: boolean
  setAlertError: (item: boolean) => void
  alertError: boolean
  cepValidation: boolean
  setCepValidation: (item: boolean) => void
  addressSelected: Addresses | undefined
  setAddressSelected: (item: Addresses | undefined) => void
  actionForm: any
  setActionForm: any
  cashBackBalance: number | null
  isLoggedIn: boolean | undefined
}

export const AccountContext = createContext<AccountContextProps>(
  {} as AccountContextProps
)

export const AccountProvider = ({ children }: AccountProviderProps) => {
  const { storageGetItem, storageSetItem } = useStorage()

  const [isLoading, setIsLoading] = useState(true)
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | undefined>()
  const [userDecathlon, setUserDecathlon] = useState<UserDecathlon | null>(null)
  const [cashBackBalance, setCashBackBalance] = useState(null)

  const [alertError, setAlertError] = useState(false)
  const [alertSuccess, setAlertSuccess] = useState(false)

  const [addressSelected, setAddressSelected] = useState<
    Addresses | undefined
  >()

  const [actionForm, setActionForm] = useState(true)
  const [cepValidation, setCepValidation] = useState(false)

  useEffect(() => {
    if (!isLoggedIn) {
      return
    }

    const getCashback = async () => {
      const { data } = await axiosClient.post('/api/getCashbackBalance', {
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'application/json',
        },
      })

      setCashBackBalance(data?.balance ?? 0)
    }

    getCashback()
  }, [isLoggedIn])

  useEffect(() => {
    const getUserDecathlon = async () => {
      const session = await getUserSession()

      setIsLoggedIn(session.isLoggedIn)

      const storageUserDecathlon = storageGetItem('user-decathlon', 'session')

      if (storageUserDecathlon) {
        setUserDecathlon(JSON.parse(storageUserDecathlon))
        setIsLoading(false)

        return
      }

      if (!session.isLoggedIn) {
        setIsLoading(false)

        return
      }

      const { data } = await axiosClient.post('/api/account/getUserDecathlon')

      setUserDecathlon(data)
      storageSetItem('user-decathlon', JSON.stringify(data), 'session')
      setIsLoading(false)
    }

    getUserDecathlon().catch(() => {
      setIsLoading(false)
      setIsLoggedIn(false)
    })
  }, [storageGetItem, storageSetItem])

  useEffect(() => {
    if (isLoggedIn && userDecathlon) {
      datadogRum.setUser({
        id: userDecathlon.getUserProfile?.sub,
        isLoggedIn: true,
        isClubMember: !!userDecathlon.getDecathlonClubStatus,
        hasCashback: (cashBackBalance ?? 0) > 0,
      })
      identifySentryUser(userDecathlon.getUserProfile?.sub)
    } else if (isLoggedIn === false) {
      datadogRum.clearUser()
      identifySentryUser(null)
    }
  }, [isLoggedIn, userDecathlon, cashBackBalance])

  const updateUserDecathlon = useCallback(async () => {
    const { data } = await axiosClient.post('/api/account/getUserDecathlon')

    setUserDecathlon(data)
    storageSetItem('user-decathlon', JSON.stringify(data), 'session')
  }, [storageSetItem])

  const contextValue = useMemo(
    () => ({
      isLoading,
      userDecathlon,
      setAlertSuccess,
      alertSuccess,
      alertError,
      setAlertError,
      setCepValidation,
      cepValidation,
      addressSelected,
      setAddressSelected,
      actionForm,
      setActionForm,
      updateUserDecathlon,
      cashBackBalance,
      isLoggedIn,
    }),
    [
      isLoading,
      userDecathlon,
      alertSuccess,
      alertError,
      cepValidation,
      addressSelected,
      actionForm,
      updateUserDecathlon,
      cashBackBalance,
      isLoggedIn,
    ]
  )

  return (
    <AccountContext.Provider value={contextValue}>
      {children}
    </AccountContext.Provider>
  )
}

export const useAccountContext = () => {
  return useContext(AccountContext)
}
